import { createContext, ReactNode, useEffect, useState } from 'react'
// hooks
import useResponsive from '../hooks/useResponsive'

// ----------------------------------------------------------------------
// Whether the operator has collapsed the nav. One boolean, and it only moves
// when the collapse control is pressed.
//
// It used to be two: a clicked state and a hovered one, with the rail
// re-expanding to full width whenever the pointer crossed it. That is a
// preference being overridden by the mouse passing over — the column an
// operator deliberately narrowed grew back under the cursor and pushed the
// page they were reading sideways, several times a minute, and there was no
// way to keep it narrow while using it. Only re-expanding goes back to full
// width now.
// ----------------------------------------------------------------------

export type CollapseDrawerContextProps = {
  isCollapse: boolean
  onToggleCollapse: VoidFunction
}

const initialState: CollapseDrawerContextProps = {
  isCollapse: false,
  onToggleCollapse: () => {},
}

const CollapseDrawerContext = createContext(initialState)

type CollapseDrawerProviderProps = {
  children: ReactNode
}

/**
 * Where the choice is kept. Collapsing the nav is a preference about how an
 * operator wants to work, so it survives a reload rather than being re-made
 * on every visit.
 */
const STORAGE_KEY = 'beast.sidebar.collapsed'

const storedCollapse = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    // Private browsing, or storage denied. The default is expanded.
    return false
  }
}

function CollapseDrawerProvider({ children }: CollapseDrawerProviderProps) {
  // `md`, matching the breakpoint the sidebar itself gates the rail on. They
  // used to disagree: this reset the choice below 1200 while the sidebar drew
  // a rail from 900, so a stored collapse reached first paint on a laptop and
  // then visibly popped back open a render later.
  const canCollapse = useResponsive('up', 'md')

  const [collapsed, setCollapsed] = useState(storedCollapse)

  useEffect(() => {
    if (!canCollapse) {
      setCollapsed(false)
    }
  }, [canCollapse])

  const handleToggleCollapse = () => {
    const next = !collapsed
    try {
      window.localStorage.setItem(STORAGE_KEY, String(next))
    } catch {
      // The preference is lost on reload, which is better than not
      // collapsing at all.
    }
    setCollapsed(next)
  }

  return (
    <CollapseDrawerContext.Provider
      value={{
        isCollapse: collapsed,
        onToggleCollapse: handleToggleCollapse,
      }}
    >
      {children}
    </CollapseDrawerContext.Provider>
  )
}

export { CollapseDrawerContext, CollapseDrawerProvider }
